import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useLabels, useRequestData, usePrefs } from 'wsm-common-data';
import { setClassNames } from 'ddc-classnames-js';
import { isBrowser } from 'ws-scripts/modules/environment';
import {
	HighlightedAttribute,
	isValidFuelType,
	getPopoverLabel,
	isInTransit,
	getInTransitLabel
} from 'wsm-highlighted-attribute';

import { VehicleViews } from 'wsm-vehicle-views';
import { ErrorBoundary } from 'wsm-error-boundary';
import {
	getVehicleTitle,
	isExistAttrConfig,
	remapPrefs,
	isVehicleMakeMatchAccountFranchise
} from '../utils/utility';
import { highlightedAttrs } from '../global-constants';

const VehicleTitle = ({ isInSidebar = false, viewsError, vehicleViews }) => {
	const labels = useLabels();

	const { deviceType, locale } = useRequestData();
	const unmappedPrefs = usePrefs();
	const prefs = isInSidebar
		? remapPrefs(unmappedPrefs, '-sidebar')
		: unmappedPrefs;
	// ^ This is bad and should probably be removed. I doubt it's necessary to be doing this mapping of prefs to change classnames for the sidebar.
	// I would consider refactoring this and adjusting styles in the design system to achieve the desired effect
	// instead of this obtuse trick.
	const { forceShowVehicleCondition } = prefs;
	const { title, vehicle, highlightedAttrsConfig } = useSelector(
		(state) => state
	);

	const displaySeoSubTitle = vehicle.seoSubTitle.length;
	const SeoSubTitleLine = displaySeoSubTitle ? (
		<span
			className={setClassNames([
				'additional-details',
				'd-block',
				'mt-0',
				'mb-1',
				'font-weight-normal',
				prefs.titleDetailsSize,
				prefs.titleDetailsColor
			])}
		>
			{vehicle.seoSubTitle}
		</span>
	) : null;

	const { estimatedDeliveryDate, status, certified, type } = vehicle;
	const shouldShowHighlightedInTransit =
		isInTransit(status) &&
		isExistAttrConfig(
			highlightedAttrsConfig,
			highlightedAttrs.ESTIMATED_DELIVERY_DATE
		);
	let inTransitText;
	if (shouldShowHighlightedInTransit) {
		inTransitText = getInTransitLabel({
			estimatedDeliveryDate,
			locale,
			labels
		});
	}

	const shouldShowConditionAsHighlightedAttribute =
		forceShowVehicleCondition === 'false';
	let vehicleConditionText;
	if (shouldShowConditionAsHighlightedAttribute) {
		if (certified && type === 'used') {
			vehicleConditionText = isVehicleMakeMatchAccountFranchise(
				highlightedAttrsConfig,
				vehicle.make
			)
				? labels
						.get('MAKE_CERTIFIED')
						.replace(/\{\{make\}\}/, vehicle.make)
				: labels.get('CERTIFIED_VEHICLE');
		} else if (type === 'new') {
			vehicleConditionText = labels.get('NEW');
		} else if (type === 'used') {
			vehicleConditionText = labels.get('USED');
		}
	}

	// If shown in-transit, the screen will not show on-site and tranfer-available
	const shouldShowOnsiteVehicle =
		!shouldShowHighlightedInTransit &&
		isExistAttrConfig(
			highlightedAttrsConfig,
			highlightedAttrs.ON_SITE_VEHICLE
		);
	let vehicleOnSiteText;
	if (shouldShowOnsiteVehicle) {
		vehicleOnSiteText = labels.get('ON_SITE_HIGHLIGHTED_ATTRIBUTE');
	}

	const shouldShowSharedVehicle =
		!shouldShowHighlightedInTransit &&
		isExistAttrConfig(
			highlightedAttrsConfig,
			highlightedAttrs.SHARED_VEHICLE
		);
	let vehicleTransferAvailableText;
	if (shouldShowSharedVehicle) {
		vehicleTransferAvailableText = labels.get(
			'OFF_SITE_HIGHLIGHTED_ATTRIBUTE'
		);
	}

	const shouldShowHighlightedFuelType =
		vehicle.normalFuelType &&
		isValidFuelType(vehicle.normalFuelType) &&
		isExistAttrConfig(highlightedAttrsConfig, highlightedAttrs.FUEL_TYPE);

	let fuelTypePopover;
	if (shouldShowHighlightedFuelType) {
		[fuelTypePopover] = getPopoverLabel({
			fuelType: vehicle.normalFuelType,
			labels
		});
	}

	const isVehicleViewsEnabled =
		isExistAttrConfig(
			highlightedAttrsConfig,
			highlightedAttrs.VEHICLE_VIEWS
		) && vehicleViews >= prefs.vehicleViews_minimumViews;

	const shouldShowVehicleClassification =
		isExistAttrConfig(
			highlightedAttrsConfig,
			highlightedAttrs.CLASSIFICATION
		) &&
		!!vehicle.classification &&
		vehicle.classification.toLowerCase() !== 'primary';

	let vehicleClassificationAttributeText;
	if (shouldShowVehicleClassification) {
		vehicleClassificationAttributeText = labels.get(
			vehicle.classification.toUpperCase()
		);
	}

	const hasAdditionalDetails =
		shouldShowHighlightedFuelType ||
		shouldShowHighlightedInTransit ||
		shouldShowOnsiteVehicle ||
		shouldShowConditionAsHighlightedAttribute ||
		shouldShowSharedVehicle ||
		// show VehicleViews
		(isVehicleViewsEnabled && !viewsError) ||
		shouldShowVehicleClassification;

	const additionalDetails = hasAdditionalDetails ? (
		// ATTN: We need spaces between <li>'s
		// This fixes a strange double click selection issue in browsers
		<ul
			className={setClassNames([
				'additional-details',
				'list-inline',
				'mb-0',
				'font-weight-normal',
				'd-flex',
				'flex-wrap',
				'ml-0',
				prefs.titleDetailsSize,
				'mt-3',
				prefs.titleDetailsColor
			])}
		>
			{shouldShowOnsiteVehicle && (
				<li className="px-0">
					<ErrorBoundary newRelicPageAction="ws-vehicle-title HighlightedAttribute error boundary">
						<HighlightedAttribute
							popoverContent={vehicleOnSiteText}
							type="in-stock"
							badgeClasses={['in-stock', 'mr-2', 'mb-2']}
						>
							{vehicleOnSiteText}
						</HighlightedAttribute>
					</ErrorBoundary>
				</li>
			)}

			{shouldShowVehicleClassification &&
				vehicleClassificationAttributeText && (
					<li className="px-0">
						<ErrorBoundary newRelicPageAction="ws-vehicle-title HighlightedAttribute error boundary">
							<HighlightedAttribute
								type="classificationName"
								badgeClasses={['default', 'mr-2', 'mb-2']}
							>
								{vehicleClassificationAttributeText}
							</HighlightedAttribute>
						</ErrorBoundary>
					</li>
				)}

			{shouldShowConditionAsHighlightedAttribute && (
				<li className="px-0">
					<ErrorBoundary newRelicPageAction="ws-vehicle-title HighlightedAttribute error boundary">
						<HighlightedAttribute
							popoverContent={vehicleConditionText}
							type="condition"
							badgeClasses={['mr-2 default', 'mb-2']}
						>
							{vehicleConditionText}
						</HighlightedAttribute>
					</ErrorBoundary>
				</li>
			)}

			{shouldShowSharedVehicle && (
				<li className="px-0">
					<ErrorBoundary newRelicPageAction="ws-vehicle-title HighlightedAttribute error boundary">
						<HighlightedAttribute
							popoverContent={vehicleTransferAvailableText}
							type="transferAvailable"
							badgeClasses={['mr-2 default', 'mb-2']}
						>
							{vehicleTransferAvailableText}
						</HighlightedAttribute>
					</ErrorBoundary>
				</li>
			)}

			{shouldShowHighlightedInTransit && (
				<li className="px-0">
					<ErrorBoundary newRelicPageAction="ws-vehicle-title HighlightedAttribute error boundary">
						<HighlightedAttribute
							popoverContent={inTransitText}
							type="inTransit"
							badgeClasses={[
								'mr-2 default',
								shouldShowHighlightedFuelType && 'mb-2'
							]}
						>
							{inTransitText}
						</HighlightedAttribute>
					</ErrorBoundary>
				</li>
			)}
			{shouldShowHighlightedFuelType && (
				<li className="px-0">
					<ErrorBoundary newRelicPageAction="ws-vehicle-title HighlightedAttribute error boundary">
						<HighlightedAttribute
							popoverContent={fuelTypePopover}
							type="fuelType"
							badgeClasses={[
								'default mr-2',
								isVehicleViewsEnabled && 'mb-2'
							]}
						>
							{vehicle.normalFuelType}
						</HighlightedAttribute>
					</ErrorBoundary>
				</li>
			)}
			{isVehicleViewsEnabled && !viewsError && (
				<li className="px-0">
					<ErrorBoundary newRelicPageAction="ws-vehicle-title VehicleViews error boundary">
						<VehicleViews
							vehicleViews={vehicleViews}
							disclaimerLabel={prefs.vehicleViews_disclaimerLabel}
							displayStyle={prefs.vehicleViews_displayStyle}
							minimumViews={prefs.vehicleViews_minimumViews}
							timeFrame={prefs.vehicleViews_timeFrame}
						/>
					</ErrorBoundary>
				</li>
			)}
			{/* This may be added back in at some point */}
			{/* {modelCode && <li> {labels.get('MODEL')}: {modelCode} </li>} */}
		</ul>
	) : null;
	const vehicleTitle = getVehicleTitle(
		title,
		labels,
		vehicle.type,
		vehicle.certified,
		prefs.forceShowVehicleCondition,
		prefs.showVehicleTitleSeparators,
		prefs.vehicleTitleSeparator
	);
	const pubsub = isBrowser && window?.DDC?.pubsub;
	useEffect(() => {
		if (pubsub && vehicleTitle) {
			pubsub?.publishDelayed(
				'ws-vehicle-toolbar/show-vehicle-title',
				vehicleTitle
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [vehicleTitle]);

	const decodeMarkup = (encodedTitle) => {
		return { __html: `${encodedTitle}` };
	};

	if (Object.keys(title).length === 0 && title.constructor === Object) {
		return null;
	}

	if (prefs.enableLineBreak === 'false') {
		return (
			<>
				<h1 className={setClassNames(['vehicle-title', 'h2', 'm-0'])}>
					<span
						// eslint-disable-next-line react/no-danger
						dangerouslySetInnerHTML={decodeMarkup(
							vehicleTitle.join(' ')
						)}
					/>
					{SeoSubTitleLine}
				</h1>
				{additionalDetails}
			</>
		);
	}

	return (
		<>
			<h1
				className={setClassNames([
					'vehicle-title',
					'm-0',
					'line-height-reset',
					deviceType !== 'DESKTOP' && 'h2'
				])}
			>
				<span
					className={setClassNames([
						'd-block',
						prefs.titleHeading,
						prefs.titleHeadingColor,
						prefs.titleHeadingWeight,
						prefs.titleHeadingSize
					])}
					// eslint-disable-next-line react/no-danger
					dangerouslySetInnerHTML={decodeMarkup(
						vehicleTitle.length > 1 && vehicleTitle[0]
					)}
				/>
				<span
					className={setClassNames([
						prefs.titleHeadingColor,
						prefs.titleHeadingTagLineWeight,
						prefs.titleHeadingTagLineSize
					])}
					// eslint-disable-next-line react/no-danger
					dangerouslySetInnerHTML={decodeMarkup(
						vehicleTitle.length > 1
							? vehicleTitle[1]
							: vehicleTitle[0]
					)}
				/>
				{SeoSubTitleLine}
			</h1>
			{additionalDetails}
		</>
	);
};

VehicleTitle.propTypes = {
	isInSidebar: PropTypes.bool,
	viewsError: PropTypes.bool.isRequired,
	vehicleViews: PropTypes.shape({}).isRequired
};

VehicleTitle.defaultProps = {
	isInSidebar: false
};

export default VehicleTitle;
